import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../context/PermissionContext';
import { Navigate } from 'react-router-dom';
import { 
  Users, Shield, Settings, Save, X, Edit, Check, 
  AlertCircle, Info, RefreshCw, Eye, EyeOff, CheckCircle,
  Plus, PlusCircle, Circle, Lock
} from 'lucide-react';

const RoleManagement = () => {
  const { hasPermission } = usePermission();
  const { currentUser } = useAuth();
  
  // Dynamic permissions - will be loaded from API
  const [allPermissions, setAllPermissions] = useState({});

  // Define default permissions for each role - will be computed from allPermissions
  const getDefaultRolePermissions = () => {
    if (!Object.keys(allPermissions).length) return {};

    const getAllPermissionCodes = () =>
      Object.values(allPermissions).flatMap(category =>
        category.permissions.map(p => p.id)
      );

    return {
      admin: getAllPermissionCodes(),
      manager: [
        // Dashboard permissions (most but not all)
        "labor_management_system", "warehouse_metrics", "truck_scheduler",
        "downstocking_report", "update_order_due_date",

        // LMS menu permissions
        "lms_clock_in", "lms_hours_stats", "lms_profile", "lms_administration",
        "lms_activities", "lms_shifts_teams", "lms_business_units", "lms_birds_eye",
        "lms_command", "lms_create_assign", "lms_time_management", "lms_kiosk",

        // User management permissions
        "view_users", "create_users", "edit_users", "view_access_logs",

        // Admin feature permissions
        "company_settings",

        // Navigation permissions
        "home_section", "company_section", "profile_section", "users_section", "reports_section"
      ].filter(code => getAllPermissionCodes().includes(code)),
      user: [
        // Dashboard permissions
        "labor_management_system", "warehouse_metrics", "truck_scheduler",

        // LMS menu permissions (limited)
        "lms_clock_in", "lms_hours_stats", "lms_profile", "lms_time_management",

        // Navigation permissions
        "home_section", "profile_section"
      ].filter(code => getAllPermissionCodes().includes(code)),
      lmsuser: [
        // Dashboard permissions
        "labor_management_system",

        // LMS menu permissions (basic)
        "lms_clock_in", "lms_hours_stats", "lms_profile",

        // Navigation permissions
        "home_section", "profile_section"
      ].filter(code => getAllPermissionCodes().includes(code))
    };
  };
  
  // API URL from environment or default
  const API_URL = process.env.REACT_APP_API_URL || 
    (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');
  
  // Define all state variables first (before any conditionals)
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [roleLoadError, setRoleLoadError] = useState(null);

  const [selectedRole, setSelectedRole] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savedMessage, setSavedMessage] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [rolePermissions, setRolePermissions] = useState({});
  const [editedPermissions, setEditedPermissions] = useState([]);
  const [permissionView, setPermissionView] = useState("grid"); // "grid" or "list"
  const [allPermissionsList, setAllPermissionsList] = useState([]);
  
  // Fetch roles from API
  const fetchRoles = async () => {
    setLoadingRoles(true);
    setRoleLoadError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      const data = await response.json();
      setRoles(data.roles);
      setLoadingRoles(false);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRoleLoadError('Failed to load roles. Please try again.');
      setLoadingRoles(false);
    }
  };
  
  // Fetch permissions by category for role management
  const fetchPermissionsByCategory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/permissions/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();
      setAllPermissions(data.permissionCategories || {});
      setAllPermissionsList(data.permissions || []);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // Fallback to empty permissions if fetch fails
      setAllPermissions({});
      setAllPermissionsList([]);
    }
  };

  // Fetch all permissions (legacy)
  const fetchAllPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();
      setAllPermissionsList(data.permissions);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };
  
  // Fetch role details
  const fetchRoleDetails = async (roleId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/roles/${roleId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      const data = await response.json();
      const role = data.role;
      const permissionCodes = role.permissions.map(p => p.permission_code);

      setRolePermissions(prev => ({
        ...prev,
        [roleId]: permissionCodes
      }));
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching role details:', error);
      setLoading(false);
    }
  };
  
  // Load data on component mount
  useEffect(() => {
    fetchRoles();
    fetchPermissionsByCategory();
    fetchAllPermissions(); // Keep for compatibility
  }, []);

  // If the user doesn't have the manage_roles permission, show permission denied
  if (!hasPermission('manage_roles')) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-8 text-center">
        <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-200 mb-2">Permission Denied</h2>
        <p className="text-slate-400 mb-6">You don't have access to manage roles and permissions.</p>
        <p className="text-slate-500 text-sm">This action requires the 'manage_roles' permission.</p>
      </div>
    );
  }
  
  // Permissions and state management

  // Local state for role permissions is defined at the top of the component

  // Handle role selection
  const handleRoleSelect = (roleId) => {
    setSelectedRole(roleId);
    setIsEditMode(false);
    setSavedMessage(null);
    
    // If we don't have permissions for this role yet, fetch them
    if (!rolePermissions[roleId]) {
      fetchRoleDetails(roleId);
    } else {
      setEditedPermissions([...rolePermissions[roleId]]);
    }
  };

  // Handle edit mode toggle
  const handleToggleEditMode = () => {
    if (isEditMode) {
      // If we're currently in edit mode, toggle back to view mode
      setIsEditMode(false);
      setEditedPermissions([]);
    } else {
      // Otherwise, enter edit mode and initialize edited permissions
      setIsEditMode(true);
      setEditedPermissions([...rolePermissions[selectedRole]]);
    }
    setSavedMessage(null);
  };

  // Handle permission toggle
  const handlePermissionToggle = (permissionId) => {
    if (editedPermissions.includes(permissionId)) {
      setEditedPermissions(editedPermissions.filter(id => id !== permissionId));
    } else {
      setEditedPermissions([...editedPermissions, permissionId]);
    }
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get permission IDs from the edited permissions (codes)
      const permissionIds = editedPermissions.map(code => {
        const permission = allPermissionsList.find(p => p.code === code);
        return permission ? permission.id : null;
      }).filter(id => id !== null);

      // Update permissions for the role
      const response = await fetch(`${API_URL}/permissions/role/${selectedRole}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permissionIds })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      // Update local state
      setRolePermissions({
        ...rolePermissions,
        [selectedRole]: [...editedPermissions]
      });
      
      setIsEditMode(false);
      setSavedMessage("Permissions updated successfully!");
      
      // Refresh role data
      fetchRoleDetails(selectedRole);
      
      // Clear the success message after 3 seconds
      setTimeout(() => {
        setSavedMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error updating role permissions:', error);
      setSavedMessage("Error updating permissions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedPermissions({});
    setSavedMessage(null);
  };

  // Handle creating a new role
  const handleCreateRole = async (newRole, permissions) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get permission IDs from the permission codes
      const permissionIds = permissions.map(code => {
        const permission = allPermissionsList.find(p => p.code === code);
        return permission ? permission.id : null;
      }).filter(id => id !== null);

      // Create role via API
      const response = await fetch(`${API_URL}/roles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newRole.name,
          description: newRole.description,
          permissions: permissionIds
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      const data = await response.json();
      // Get the created role from response
      const createdRole = data.role;
      
      // Update local state
      setRoles([...roles, createdRole]);
      setRolePermissions({
        ...rolePermissions,
        [createdRole.id]: [...permissions]
      });
      
      // Close modal and show success message
      setShowCreateModal(false);
      setSavedMessage(`Role "${createdRole.name}" created successfully!`);
      
      // Select the new role
      setSelectedRole(createdRole.id);
      
      // Refresh roles list
      fetchRoles();
      
      // Clear the success message after 3 seconds
      setTimeout(() => {
        setSavedMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error creating role:', error);
      setSavedMessage("Error creating role. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Get color class for role badge
  const getRoleColorClass = (color) => {
    switch (color) {
      case 'red':
        return 'bg-red-900/30 text-red-300 border-red-800/50';
      case 'yellow':
        return 'bg-yellow-900/30 text-yellow-300 border-yellow-800/50';
      case 'green':
        return 'bg-green-900/30 text-green-300 border-green-800/50';
      case 'blue':
      default:
        return 'bg-blue-900/30 text-blue-300 border-blue-800/50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Shield className="w-6 h-6 text-blue-400 mr-3" />
            <h2 className="text-xl font-semibold text-slate-200">Role Management</h2>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => setPermissionView(permissionView === "grid" ? "list" : "grid")}
              className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300"
              title={permissionView === "grid" ? "Switch to list view" : "Switch to grid view"}
            >
              {permissionView === "grid" ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <p className="text-slate-400 mt-2">Manage roles and permissions to control access to different features of the system.</p>
      </div>

      {/* Main content area */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Role list column */}
        <div className="lg:w-1/3">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm h-full">
            <div className="p-4 border-b border-slate-700">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-md font-medium text-slate-200">Available Roles</h3>
                  <p className="text-sm text-slate-400 mt-1">Select a role to manage its permissions</p>
                </div>
                {hasPermission('manage_roles') && (
                  <button 
                    onClick={() => setShowCreateModal(true)}
                    className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white flex items-center gap-1"
                    title="Create New Role"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm">New</span>
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-4 space-y-3">
              {loadingRoles ? (
                <div className="flex justify-center items-center h-40">
                  <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
                </div>
              ) : roleLoadError ? (
                <div className="p-4 bg-red-900/30 border border-red-800 rounded-lg text-center">
                  <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                  <p className="text-red-300">{roleLoadError}</p>
                  <button 
                    onClick={fetchRoles}
                    className="mt-3 px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded text-slate-300 text-sm"
                  >
                    Try Again
                  </button>
                </div>
              ) : roles.length === 0 ? (
                <div className="p-4 bg-slate-750 border border-slate-700 rounded-lg text-center">
                  <Shield className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <p className="text-slate-400">No roles found</p>
                  <button 
                    onClick={() => setShowCreateModal(true)}
                    className="mt-3 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm flex items-center gap-1 mx-auto"
                  >
                    <Plus className="w-4 h-4" />
                    Create First Role
                  </button>
                </div>
              ) : (
                roles.map(role => (
                  <div 
                    key={role.id}
                    onClick={() => handleRoleSelect(role.id)}
                    className={`p-4 rounded-lg cursor-pointer transition-colors
                      ${selectedRole === role.id ? 'bg-blue-900/30 border border-blue-700' : 'hover:bg-slate-750 bg-slate-750/50 border border-transparent'}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleColorClass(role.color || 'blue')}`}>
                            {role.name}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-slate-300">{role.description}</p>
                      </div>
                      {selectedRole === role.id && (
                        <div className="bg-blue-500 rounded-full p-1">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Permissions column */}
        <div className="lg:w-2/3">
          {selectedRole ? (
            <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
              <div className="p-4 border-b border-slate-700 flex justify-between items-center">
                <div>
                  <h3 className="text-md font-medium text-slate-200">
                    {roles.find(r => r.id === selectedRole)?.name} Permissions
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">
                    Manage which features this role can access
                  </p>
                </div>
                
                {hasPermission('manage_roles') && !loading && (
                  <div>
                    {isEditMode ? (
                      <div className="flex gap-2">
                        <button 
                          onClick={handleCancelEdit}
                          className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={handleSaveChanges}
                          className="flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg gap-1"
                          disabled={loading}
                        >
                          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          <span>Save Changes</span>
                        </button>
                      </div>
                    ) : (
                      <button 
                        onClick={handleToggleEditMode}
                        className="flex items-center px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg gap-1"
                      >
                        <Edit className="w-4 h-4" />
                        <span>Edit Permissions</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Saved message */}
              {savedMessage && (
                <div className="mx-4 mt-4 p-3 bg-green-900/30 border border-green-800 rounded-md text-green-300 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {savedMessage}
                </div>
              )}
              
              {/* Loading state */}
              {loading && (
                <div className="flex justify-center items-center p-8">
                  <div className="text-center">
                    <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" />
                    <p className="text-slate-400">Loading permissions...</p>
                  </div>
                </div>
              )}
              
              {/* Permissions grid/list */}
              {!loading && (
                <div className="p-4">
                  {permissionView === "grid" ? (
                    // Grid view
                    <div className="space-y-6">
                      {Object.entries(allPermissions).map(([categoryKey, category]) => (
                        <div key={categoryKey} className="bg-slate-750 rounded-lg overflow-hidden">
                          <div className="p-4 bg-slate-700/50 border-b border-slate-700">
                            <h4 className="font-medium text-slate-200">{category.title}</h4>
                            <p className="text-sm text-slate-400 mt-1">{category.description}</p>
                          </div>
                          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                            {category.permissions.map(permission => {
                              const isEnabled = isEditMode
                                ? editedPermissions.includes(permission.id)
                                : rolePermissions[selectedRole]?.includes(permission.id);
                              
                              return (
                                <div 
                                  key={permission.id}
                                  className={`p-3 border rounded-lg ${
                                    isEnabled 
                                      ? 'bg-blue-900/20 border-blue-700/50' 
                                      : 'bg-slate-800 border-slate-700'
                                  } ${isEditMode ? 'cursor-pointer' : ''}`}
                                  onClick={isEditMode ? () => handlePermissionToggle(permission.id) : undefined}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center">
                                        <span className="font-medium text-slate-200">{permission.name}</span>
                                        <div 
                                          className="ml-2 text-slate-400 cursor-help"
                                          title={permission.description}
                                        >
                                          <Info className="w-3 h-3" />
                                        </div>
                                      </div>
                                      <p className="text-xs text-slate-400 mt-1">{permission.description}</p>
                                    </div>
                                    
                                    <div className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                                      isEnabled
                                        ? 'bg-blue-600 border-blue-500'
                                        : 'bg-slate-700 border-slate-600'
                                    }`}>
                                      {isEnabled && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // List view
                    <div className="bg-slate-750 rounded-lg overflow-hidden">
                      <table className="w-full text-left">
                        <thead className="bg-slate-700/50 text-xs font-medium text-slate-300 uppercase">
                          <tr>
                            <th className="px-4 py-3">Permission</th>
                            <th className="px-4 py-3">Category</th>
                            <th className="px-4 py-3">Description</th>
                            <th className="px-4 py-3 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                          {Object.entries(allPermissions).flatMap(([categoryKey, category]) => 
                            category.permissions.map(permission => {
                              const isEnabled = isEditMode
                                ? editedPermissions.includes(permission.id)
                                : rolePermissions[selectedRole]?.includes(permission.id);
                              
                              return (
                                <tr 
                                  key={permission.id}
                                  className={`hover:bg-slate-700/30 ${isEditMode ? 'cursor-pointer' : ''}`}
                                  onClick={isEditMode ? () => handlePermissionToggle(permission.id) : undefined}
                                >
                                  <td className="px-4 py-3 font-medium text-slate-200">{permission.name}</td>
                                  <td className="px-4 py-3 text-slate-400">{category.title}</td>
                                  <td className="px-4 py-3 text-slate-400">{permission.description}</td>
                                  <td className="px-4 py-3 text-center">
                                    <div className="flex justify-center">
                                      <div className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                                        isEnabled
                                          ? 'bg-blue-600 border-blue-500'
                                          : 'bg-slate-700 border-slate-600'
                                      }`}>
                                        {isEnabled && <Check className="w-3 h-3 text-white" />}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-8 flex flex-col items-center justify-center h-full">
              <Shield className="w-16 h-16 text-slate-600 mb-4" />
              <h3 className="text-xl font-medium text-slate-300 mb-2">Select a Role</h3>
              <p className="text-slate-400 text-center max-w-md">
                Choose a role from the left panel to view and manage its permissions
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Create Role Modal */}
      <CreateRoleModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateRole}
        allPermissions={allPermissions}
        loading={loading}
      />
    </div>
  );
};

// Create Role Modal Component
const CreateRoleModal = ({ isOpen, onClose, onSubmit, allPermissions, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: 'blue',
    permissions: []
  });
  
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState(Object.keys(allPermissions)[0] || '');
  
  const colors = [
    { id: 'blue', name: 'Blue', class: 'bg-blue-900/30 text-blue-300 border-blue-800/50' },
    { id: 'red', name: 'Red', class: 'bg-red-900/30 text-red-300 border-red-800/50' },
    { id: 'green', name: 'Green', class: 'bg-green-900/30 text-green-300 border-green-800/50' },
    { id: 'yellow', name: 'Yellow', class: 'bg-yellow-900/30 text-yellow-300 border-yellow-800/50' },
    { id: 'purple', name: 'Purple', class: 'bg-purple-900/30 text-purple-300 border-purple-800/50' },
    { id: 'pink', name: 'Pink', class: 'bg-pink-900/30 text-pink-300 border-pink-800/50' },
    { id: 'indigo', name: 'Indigo', class: 'bg-indigo-900/30 text-indigo-300 border-indigo-800/50' },
    { id: 'teal', name: 'Teal', class: 'bg-teal-900/30 text-teal-300 border-teal-800/50' }
  ];
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  const handlePermissionToggle = (permissionId) => {
    if (formData.permissions.includes(permissionId)) {
      setFormData(prev => ({
        ...prev, 
        permissions: prev.permissions.filter(id => id !== permissionId)
      }));
    } else {
      setFormData(prev => ({
        ...prev, 
        permissions: [...prev.permissions, permissionId]
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Role description is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    onSubmit(formData);
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: 'blue',
      permissions: []
    });
    setErrors({});
    setActiveTab(Object.keys(allPermissions)[0] || '');
  };
  
  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-lg shadow-xl border border-slate-700 w-full max-w-4xl my-8">
            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-700">
              <h3 className="text-lg font-medium text-slate-200">
                Create New Role
              </h3>
              <button 
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                className="text-slate-400 hover:text-slate-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Role details column */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-slate-200 border-b border-slate-700 pb-2">Role Details</h4>
              
              {/* Role Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-1">
                  Role Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Warehouse Manager"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                )}
              </div>
              
              {/* Role Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-slate-300 mb-1">
                  Role Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe the purpose and responsibilities of this role"
                ></textarea>
                {errors.description && (
                  <p className="mt-1 text-sm text-red-400">{errors.description}</p>
                )}
              </div>
              
              {/* Role Color */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Role Color
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {colors.map(color => (
                    <div 
                      key={color.id}
                      onClick={() => setFormData(prev => ({ ...prev, color: color.id }))}
                      className={`
                        cursor-pointer rounded-md p-2 border
                        ${formData.color === color.id ? 'ring-2 ring-blue-500' : ''}
                        ${color.class}
                      `}
                    >
                      <div className="text-center text-xs">{color.name}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Role Badge Preview */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Badge Preview
                </label>
                <div className="p-4 bg-slate-750 rounded-lg border border-slate-700">
                  <span className={`
                    inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border
                    ${colors.find(c => c.id === formData.color)?.class}
                  `}>
                    {formData.name || 'Role Name'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Permissions column */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-slate-200 border-b border-slate-700 pb-2">Basic Permissions</h4>
              
              {/* Permission Categories */}
              <div className="flex border-b border-slate-700">
                {Object.entries(allPermissions).map(([key, category], index) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveTab(key)}
                    className={`px-4 py-2 text-sm ${
                      activeTab === key 
                        ? 'border-b-2 border-blue-500 text-blue-400' 
                        : 'text-slate-400 hover:text-slate-300'
                    }`}
                  >
                    {category.title}
                  </button>
                ))}
              </div>
              
              {/* Permissions Grid */}
              <div className="bg-slate-750 rounded-lg p-4 h-96 overflow-y-auto">
                {Object.keys(allPermissions).length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" />
                      <p className="text-slate-400">Loading permissions...</p>
                    </div>
                  </div>
                ) : activeTab && allPermissions[activeTab] ? (
                  <>
                    <p className="text-sm text-slate-400 mb-3">
                      {allPermissions[activeTab].description}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {allPermissions[activeTab].permissions.map(permission => {
                        const isEnabled = formData.permissions.includes(permission.id);

                        return (
                          <div
                            key={permission.id}
                            className={`p-3 border rounded-lg cursor-pointer ${
                              isEnabled
                                ? 'bg-blue-900/20 border-blue-700/50'
                                : 'bg-slate-800 border-slate-700'
                            }`}
                            onClick={() => handlePermissionToggle(permission.id)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center">
                                  <span className="font-medium text-slate-200">{permission.name}</span>
                                  <div
                                    className="ml-2 text-slate-400 cursor-help"
                                    title={permission.description}
                                  >
                                    <Info className="w-3 h-3" />
                                  </div>
                                </div>
                                <p className="text-xs text-slate-400 mt-1">{permission.description}</p>
                              </div>

                              <div className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                                isEnabled
                                  ? 'bg-blue-600 border-blue-500'
                                  : 'bg-slate-700 border-slate-600'
                              }`}>
                                {isEnabled && <Check className="w-3 h-3 text-white" />}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-slate-400 text-center">No permissions found for this category.</p>
                )}
              </div>
              
              <p className="text-xs text-slate-400">
                Tip: Additional permissions can be configured after creating the role.
              </p>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3 border-t border-slate-700 pt-4">
            <button
              type="button"
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="px-4 py-2 text-sm font-medium bg-slate-700 hover:bg-slate-600 rounded-md text-slate-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 rounded-md text-white flex items-center gap-1"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <PlusCircle className="w-4 h-4" />
              )}
              Create Role
            </button>
          </div>
        </form>
      </div>
    </div>
    )}
    </>
  );
};


export default RoleManagement;
